import { TUNING } from '~/config/tuning'
import { seededRandom, shuffle, weightedPick } from '~/utils/random'
import { drawThroughCycle } from '~/utils/offerCycle'
import { ELEMENTS, type CreatureDef, type RunState, type ElementKey } from '~/types/game'

const def = (
  id: string,
  name: string,
  fire: number,
  water: number,
  grass: number,
  lightning: number,
  earth: number,
  speed: number,
): CreatureDef => ({ id, name, stats: { fire, water, grass, lightning, earth }, speed, rarity: 'uncommon' })

/** 🌟 Rare roster helper (v1.01): +1 total point over the baseline and
 * one signature perk each (speed 5, innate ⚔️ Fight, or a dual splash). */
const rare = (
  id: string,
  name: string,
  fire: number,
  water: number,
  grass: number,
  lightning: number,
  earth: number,
  speed: number,
  fight: number,
  blurb: string,
): CreatureDef => ({
  id,
  name,
  stats: { fire, water, grass, lightning, earth },
  speed,
  fight,
  rarity: 'rare',
  blurb,
})

/**
 * 🧬 Expansion uncommons (v2.80) — the first tranche of the creature
 * roster proposal's `Y` set. Same point budget as the baseline 24, but
 * each carries EITHER its own passive or its own on-use skill in place
 * of Surge, never both (Javi's rule). Art is deferred: with no poster
 * these fall back to the element-icon placeholder everywhere.
 */
const expansion = (
  id: string,
  name: string,
  fire: number,
  water: number,
  grass: number,
  lightning: number,
  earth: number,
  speed: number,
  extra: { passive?: string; skill?: string; blurb?: string; fight?: number },
): CreatureDef => ({
  id,
  name,
  stats: { fire, water, grass, lightning, earth },
  speed,
  rarity: 'uncommon',
  ...extra,
  // A creature with its own on-use skill has no Surge to fall back on.
  ...(extra.skill ? { noSurge: true } : {}),
})

/** 🌟 Expansion RARE helper (v2.87): same shape as `expansion`, but on
 * the rare rarity so it sells in the shop and rolls at rare weight. */
const expansionRare = (
  id: string,
  name: string,
  fire: number,
  water: number,
  grass: number,
  lightning: number,
  earth: number,
  speed: number,
  extra: { passive?: string; skill?: string; blurb?: string; fight?: number },
): CreatureDef => ({
  id,
  name,
  stats: { fire, water, grass, lightning, earth },
  speed,
  rarity: 'rare',
  ...extra,
  ...(extra.skill ? { noSurge: true } : {}),
})

/** 💜 Expansion EPIC helper (v2.94): elevated statlines (7 points). */
const expansionEpic = (
  id: string, name: string,
  fire: number, water: number, grass: number, lightning: number, earth: number,
  speed: number,
  extra: { passive?: string; skill?: string; blurb?: string; fight?: number },
): CreatureDef => ({
  id, name, stats: { fire, water, grass, lightning, earth }, speed,
  rarity: 'epic', ...extra, ...(extra.skill ? { noSurge: true } : {}),
})

/** 🧡 Expansion LEGENDARY helper (v3.03): nine-point statlines. */
const expansionLegendary = (
  id: string, name: string,
  fire: number, water: number, grass: number, lightning: number, earth: number,
  speed: number,
  extra: { passive?: string; skill?: string; blurb?: string; fight?: number },
): CreatureDef => ({
  id, name, stats: { fire, water, grass, lightning, earth }, speed,
  rarity: 'legendary', ...extra, ...(extra.skill ? { noSurge: true } : {}),
})

/**
 * Starting creature pool (gamedesign.md §6). Speed is innate travel pace:
 * 2 = heavy, 3 = typical, 4 = nimble. The baseline sits mid-scale on
 * purpose: 1 is reserved for future extra-slow bulky designs and 5+ for
 * extra-fast glass cannons.
 */
export const CREATURE_POOL: CreatureDef[] = [
  def('ember-cub', 'Ember Cub', 3, 0, 0, 0, 1, 3),
  def('sproutling', 'Sproutling', 0, 1, 3, 0, 0, 3),
  def('sparkmouse', 'Sparkmouse', 0, 0, 0, 3, 1, 4),
  def('mudfin', 'Mudfin', 0, 2, 0, 0, 2, 3),
  def('pebble-pup', 'Pebble Pup', 0, 0, 1, 0, 3, 2),
  def('bubble-bat', 'Bubble Bat', 0, 3, 0, 1, 0, 4),
  def('cinder-moth', 'Cinder Moth', 2, 0, 0, 2, 0, 4),
  def('root-toad', 'Root Toad', 0, 0, 2, 0, 2, 2),
  def('storm-kit', 'Storm Kit', 1, 0, 0, 3, 0, 4),
  def('reed-otter', 'Reed Otter', 0, 2, 2, 0, 0, 3),
  def('lava-newt', 'Lava Newt', 2, 0, 0, 0, 2, 3),
  def('dew-hare', 'Dew Hare', 0, 3, 1, 0, 0, 4),
  // Pure specialists: strong but narrow (gamedesign.md §2.2).
  def('pyre-hound', 'Pyre Hound', 4, 0, 0, 0, 0, 4),
  def('brook-seal', 'Brook Seal', 0, 4, 0, 0, 0, 3),
  def('grove-snail', 'Grove Snail', 0, 0, 4, 0, 0, 2),
  def('volt-finch', 'Volt Finch', 0, 0, 0, 4, 0, 4),
  def('terra-tort', 'Terra Tort', 0, 0, 0, 0, 4, 2),
  // Pairs that were missing from the original pool.
  def('steam-toad', 'Steam Toad', 2, 2, 0, 0, 0, 3),
  def('pepper-sprout', 'Pepper Sprout', 2, 0, 2, 0, 0, 3),
  def('glow-moss', 'Glow Moss', 0, 0, 2, 2, 0, 2),
  // Spread creatures: flexible but rarely carry alone.
  def('prism-fox', 'Prism Fox', 1, 1, 1, 1, 1, 3),
  def('soot-sprite', 'Soot Sprite', 2, 0, 0, 1, 1, 3),
  def('marsh-wisp', 'Marsh Wisp', 0, 2, 1, 0, 1, 3),
  def('gale-koi', 'Gale Koi', 0, 1, 0, 2, 1, 3),

  // ---- 🌟 RARE roster (v1.01, per Javi): the PG-13 designs, bought
  // with Stardust in the title-screen shop (owned forever). Each runs
  // 5 total points (+1 over baseline) plus one signature perk. ----
  rare('cinderfang', 'Cinderfang', 4, 0, 0, 1, 0, 4, 2,
    'A saber-maned inferno cat — fast, mean, and always mid-pounce.'),
  rare('ashwing', 'Ashwing', 3, 0, 0, 2, 0, 5, 0,
    'A firestorm moth that outruns its own embers. Nothing travels faster.'),
  rare('stormclaw', 'Stormclaw', 1, 0, 0, 4, 0, 4, 3,
    'A lightning-pelted feline that ends fights before they start.'),
  rare('voltgnaw', 'Voltgnaw', 0, 0, 0, 3, 2, 4, 2,
    'A capacitor-cheeked rodent that chews through anything grounded.'),
  rare('marshfang', 'Marshfang', 0, 3, 2, 0, 0, 4, 0,
    'A slime-slicked bog drake, veins aglow with deep-marsh light — the current given teeth.'),
  rare('misthorn', 'Misthorn', 0, 4, 1, 0, 0, 5, 0,
    'A fog-antlered hare that runs on rain. The courier of couriers.'),
  rare('ripwing', 'Ripwing', 0, 3, 0, 2, 0, 4, 2,
    'A storm-surfing ray that strikes where the tide meets the thunder.'),
  rare('bogmaw', 'Bogmaw', 0, 2, 3, 0, 0, 3, 3,
    'A swamp jaw with a garden on its back. Slow to anger; quick to bite.'),
  rare('bramblehorn', 'Bramblehorn', 0, 0, 3, 0, 2, 3, 2,
    'A thicket-antlered stag whose bark is armor and whose charge is worse.'),
  rare('stoneback', 'Stoneback', 0, 0, 1, 0, 4, 2, 4,
    'A walking rampart. The mountain does not wait — it fights faster than it walks.'),

  // 🧬 Expansion, tranche 1 COMPLETE (v2.86): all 13 uncommons of the
  // proposal's Y set — nine carrying their own passive, four carrying an
  // on-use skill in place of Surge.
  expansion('hollowbark-stag', 'Hollowbark Stag', 0, 0, 2, 0, 2, 3, {
    passive: 'rootstock',
    blurb: 'Antlers like a winter canopy. Where it stands, the whole grove feeds.',
  }),
  expansion('sunfledge', 'Sunfledge', 2, 0, 0, 2, 0, 4, {
    passive: 'morning-chorus',
    blurb: 'It sings the sun up, and the morning takes its time in answering.',
  }),
  // v3.53 (per Javi): renamed to Pyroquill. Like Fernmother, the id stays
  // put so saves, art, collection records and favorites all survive.
  expansion('cinderquill', 'Pyroquill', 3, 0, 0, 0, 1, 3, {
    passive: 'ashbed',
    blurb: 'Every defeat banks another ember. It has never wasted a loss.',
  }),
  expansion('loamgrub', 'Loamgrub', 0, 0, 1, 0, 3, 2, {
    passive: 'deep-sit',
    blurb: 'Give it ten quiet seconds and it becomes part of the hill.',
  }),
  expansion('mosswick', 'Mosswick', 0, 1, 3, 0, 0, 3, {
    skill: 'graft',
    blurb: 'It gives pieces of itself away, and the giving is the point.',
  }),
  expansion('flarelet', 'Flarelet', 2, 0, 0, 2, 0, 4, {
    passive: 'first-light',
    blurb: 'First out of the gate, every time, burning brightest at the start.',
  }),
  expansion('thistlemote', 'Thistlemote', 0, 0, 2, 2, 0, 3, {
    passive: 'thornmail',
    blurb: 'A burr with opinions. Losing to it still costs you something.',
  }),
  expansion('gustfin', 'Gustfin', 0, 2, 0, 2, 0, 4, {
    passive: 'tailwind',
    blurb: 'It leaves a following wind behind it, and the column runs faster for it.',
  }),
  expansion('rillhopper', 'Rillhopper', 0, 3, 1, 0, 0, 4, {
    skill: 'skim',
    blurb: 'It does not cross the water so much as decline to sink.',
  }),
  // v4.66 (Javi's balance pass): promoted to RARE — Scrapfire earns its
  // keep in the expiry meta now.
  expansionRare('coalpip', 'Coalpip', 3, 0, 0, 1, 2, 3, {
    passive: 'scrapfire',
    blurb: 'A scavenger of lost causes. Every missed chance feeds its fire.',
  }),
  // v3.18 (per Javi): the gauntlet sweep outed the healer as a carry —
  // promoted to LEGENDARY with a name to match. The id stays 'fernkit'
  // so saves, art, collection records and favorites all survive.
  // v4.66 (Javi's balance pass): a legendary statline at last — the
  // rarity was promoted in v3.18 but the line never followed.
  expansionLegendary('fernkit', 'Fernmother, the First Bloom', 0, 1, 5, 2, 0, 3, {
    skill: 'bloom',
    blurb: 'It unfurls once, spectacularly, and somebody walks away whole.',
  }),
  expansion('pyrepup', 'Pyrepup', 4, 0, 0, 0, 0, 4, {
    skill: 'overheat',
    blurb: 'All of it, all at once, and then nothing at all for a while.',
  }),
  expansion('dunemite', 'Dunemite', 0, 0, 0, 2, 2, 3, {
    passive: 'grit',
    // v4.66 (per Javi): a solo specialist carries an inherent Fight point.
    fight: 1,
    blurb: 'Sent alone into the sand, and it prefers it that way.',
  }),
  // ── 🌟 Expansion RARES, tranche 2a (v2.87): twelve whose passives ride
  // hooks the engine already has. The other fourteen Y rares need new
  // engine events (teammate level-up, cross-mission chance, charge
  // stockpiles) and follow in 2b. ──
  // v4.66 (Javi's balance pass): EPIC — Kilnheart compounds too hard for rare.
  expansionEpic('kilnwyrm', 'Kilnwyrm', 5, 0, 0, 2, 0, 4, {
    passive: 'forgeheart',
    blurb: 'It eats the heat of every fire it did not start.',
  }),
  expansionRare('slagbeast', 'Slagbeast', 3, 0, 0, 0, 2, 2, {
    passive: 'heat-death',
    blurb: 'What it kills, it keeps. Bosses are simply the richest meals.',
  }),
  expansionRare('forge-calf', 'Forge Calf', 3, 0, 0, 0, 2, 2, {
    passive: 'kilnheart',
    blurb: 'Raised in the coals of an elite forge, and hammered by every one since.',
  }),
  expansionRare('wispkeeper', 'Wispkeeper', 1, 0, 2, 2, 0, 4, {
    passive: 'soulbind',
    blurb: 'It collects the shape of every fight it lost, and wears them.',
  }),
  expansionRare('gildmaw', 'Gildmaw', 0, 0, 0, 1, 4, 2, {
    passive: 'prospector',
    blurb: 'It can smell a paying job through solid rock.',
  }),
  expansionRare('barrow-knight', 'Barrow Knight', 0, 0, 1, 0, 4, 2, {
    passive: 'last-stand',
    blurb: 'It only truly wakes when the situation is already hopeless.',
  }),
  // v3.53 (per Javi): the karaoke bit is gone — it is a Tidebeacon now,
  // the light the coast steers by. Id unchanged, see Pyroquill above.
  expansionRare('karaoke-kraken', 'Tidebeacon', 0, 3, 0, 2, 0, 3, {
    passive: 'crowd-work',
    blurb: 'Nine arms, one lantern, and a light the whole coast steers by.',
  }),
  expansionRare('pebblet', 'Pebblet', 0, 0, 1, 0, 4, 2, {
    passive: 'underdog',
    blurb: 'The smallest thing on the field, and it has noticed.',
  }),
  // 🐙 Renamed from "Mirroring" in v4.23 (per Javi: "mirroring is kinda
  // lame — it's an octopus-like creature"). The id stays 'mirroring':
  // saves, the collection codex and the art files all key on it.
  expansionRare('mirroring', 'Echopus', 0, 3, 1, 1, 0, 4, {
    passive: 'reflection',
    blurb: 'It borrows whatever the room is best at, and gives none of it back.',
  }),
  expansionRare('naptoad', 'Naptoad', 0, 2, 2, 0, 1, 2, {
    passive: 'power-nap',
    blurb: 'Fifteen seconds is all it asks, and it is very firm about that.',
  }),
  expansionRare('gilt-minnow', 'Gilt Minnow', 0, 4, 0, 0, 1, 4, {
    passive: 'gilded-scales',
    blurb: 'Merchants see the scales and remember an old, generous debt.',
  }),
  expansionRare('mosskit', 'Mosskit', 0, 1, 4, 0, 0, 3, {
    passive: 'green-thumb',
    blurb: 'Wherever it naps, the campsite grows back greener.',
  }),
  // ── 🌟 Expansion RARES, tranche 2b (v2.91): six more on the new
  // resolution-pass hooks. Featherweight, Routine, Will-o'-Wisp,
  // Infectious Giggle and the three on-use skills wait for 2c. ──
  expansionRare('springtail', 'Springtail', 0, 0, 3, 2, 0, 4, {
    passive: 'second-wind',
    blurb: 'It comes back from every trip somehow taller than it left.',
  }),
  // 🐐 v5.80 (per Javi): reborn as the STORMGLASS IBEX - same id so
  // saves, ownership and boards never notice; new name, art and blurb.
  expansionRare('sparkvein-hare', 'Stormglass Ibex', 0, 0, 0, 4, 1, 5, {
    passive: 'static-charge',
    blurb: 'Its stormglass horns drink the lightning; every finished job leaves one more bolt caged inside.',
  }),
  expansionRare('rootwarden', 'Rootwarden', 0, 1, 4, 0, 0, 3, {
    passive: 'evergrowth',
    blurb: 'It plants nothing. It simply grows wherever its friends do.',
  }),
  expansionRare('stormsinger', 'Stormsinger', 0, 1, 0, 4, 0, 4, {
    passive: 'resonance',
    blurb: 'Its song carries across the whole field, and everyone works faster for hearing it.',
  }),
  expansionRare('silt-colossus', 'Silt Colossus', 0, 2, 0, 0, 3, 2, {
    passive: 'ballast',
    blurb: 'A full boat sits deeper in the water, and deeper is exactly where it likes to be.',
  }),
  // v5.11 (per Javi): back to 1/1/1/1/1 — Equanimity itself was
  // redesigned instead (a pool of FLOATING points that auto-fill the
  // assigned mission's gaps; see equanimityFill in battle.ts). The
  // v5.10 statline reshape lasted one release: the passive was the
  // problem, not the monk.
  expansionRare('hollow-sage', 'Hollow Sage', 1, 1, 1, 1, 1, 3, {
    passive: 'equanimity',
    blurb: 'What it lacks, it has simply chosen not to carry.',
  }),
  // ── 🌟 Expansion RARES, tranche 2c (v2.92): the three skill carriers
  // plus the two ride-along passives. Featherweight (Foehn Sylph) and
  // Routine (Mirror Mime) close the tier in 2d. ──
  // v4.66 (Javi's balance pass): EPIC — Tithe scales with the gold curve.
  expansionEpic('ashen-vicar', 'Ashen Vicar', 4, 0, 0, 0, 3, 3, {
    skill: 'tithe',
    blurb: 'It accepts donations on behalf of the fire, and the fire provides.',
  }),
  expansionRare('cragjaw', 'Cragjaw', 0, 0, 0, 1, 4, 2, {
    skill: 'bulwark-drop',
    blurb: 'Once a night it decides, in advance, that something will not hurt.',
  }),
  expansionRare('mirrorfin', 'Mirrorfin', 0, 3, 0, 0, 2, 4, {
    skill: 'refract',
    blurb: 'Hold it up to a friend and it becomes them, briefly and completely.',
  }),
  expansionRare('ghost-lantern', 'Ghost Lantern', 2, 0, 0, 3, 0, 4, {
    passive: 'will-o-wisp',
    blurb: 'Every mission that slips away leaves a small light in its keeping.',
  }),
  // v3.53 (per Javi): Briarflare, and no more giggling anywhere near it.
  // Id unchanged, see Pyroquill above.
  expansionRare('gigglemoth', 'Briarflare', 1, 0, 2, 2, 0, 4, {
    passive: 'infectious-giggle',
    blurb: 'It settles on a briar, and the whole hedge remembers fire.',
  }),
  // ── 🌟 Expansion RARES, tranche 2d (v2.93): the tier's last two. ──
  expansionRare('foehn-sylph', 'Foehn Sylph', 0, 1, 0, 4, 0, 5, {
    passive: 'featherweight',
    blurb: 'It weighs nothing, promises nothing, and fits anywhere.',
  }),
  // v4.66 (Javi's balance pass): EPIC — the copy grew with every buffed
  // teammate. The flat line IS the identity; speed carries the bump.
  expansionEpic('mirror-mime', 'Mirror Mime', 1, 1, 1, 1, 1, 4, {
    passive: 'routine',
    blurb: 'It follows whoever left last, and becomes them by watching.',
  }),
  // ── 💜 Expansion EPICS, tranche 3a (v2.94): six on existing hooks. ──
  expansionEpic('pyrelord-ossuary', 'Pyrelord Ossuary', 5, 0, 0, 0, 2, 3, {
    passive: 'cremation',
    blurb: 'It tends the missions nobody answered, and burns brighter for each.',
  }),
  expansionEpic('solar-kirin', 'Solar Kirin', 4, 0, 2, 0, 0, 4, {
    passive: 'zenith',
    blurb: 'Where it succeeds, the day simply counts for more.',
  }),
  // 🧊 Legendary as of v5.20 (per Javi) — the passive is the payload.
  expansionLegendary('glacier-warden', 'Glacier Warden', 0, 3, 1, 0, 5, 2, {
    passive: 'permafrost',
    blurb: 'Nothing wounds what it watches. The cold keeps a tally.',
  }),
  expansionEpic('ember-reliquary', 'Ember Reliquary', 4, 0, 0, 0, 3, 2, {
    passive: 'reliquary',
    blurb: 'Every treasure you carry, it carries hotter.',
  }),
  // v3.17 (per Javi): promoted to LEGENDARY — the gauntlet duo sweep and
  // the post-v3.16 retest both put her a full tier above every epic.
  // v3.18 (per Javi): statline 2s → 1s — the bloom compounds; the
  // opening hand shouldn't also be broad.
  expansionLegendary('duskmoth-empress', 'Duskmoth Empress', 2, 2, 2, 2, 2, 4, {
    passive: 'eclipse-bloom',
    blurb: 'Each nightfall leaves her a little more of everything.',
  }),
  expansionEpic('dewdrop-axolotl', 'Dewdrop Axolotl', 0, 5, 2, 0, 0, 3, {
    passive: 'osmosis',
    blurb: 'It absorbs a little of every victory, weakest places first.',
  }),
  // ── 💜 Expansion EPICS, tranche 3b (v2.95). ──
  // v4.66 (Javi's balance pass): down to RARE — a bench passive, not an
  // epic carry.
  expansionRare('cinderowl', 'Cinderowl', 4, 0, 0, 2, 0, 4, {
    passive: 'night-watch',
    blurb: 'While it watches from home, deadlines are simply slower to arrive.',
  }),
  expansionEpic('loamshell-crab', 'Loamshell Crab', 0, 2, 0, 0, 5, 2, {
    passive: 'bedrock-memory',
    blurb: 'Potential it has not spent is armor it already wears.',
  }),
  expansionEpic('barrowfur-bear', 'Barrowfur Bear', 0, 0, 2, 0, 5, 2, {
    passive: 'hibernal-might',
    blurb: 'Wake it early and it is a bear. Let it sleep and it is a season.',
  }),
  expansionEpic('prismarine-eel', 'Prismarine Eel', 0, 5, 0, 2, 0, 4, {
    passive: 'refraction2',
    blurb: 'Every charm you own, it sees twice — and keeps both reflections.',
  }),
  expansionEpic('ironvine-colossus', 'Ironvine Colossus', 0, 0, 4, 0, 3, 2, {
    passive: 'overgrowth',
    blurb: 'Wherever it stands, there is somehow room for one more.',
  }),
  // ── 💜 Expansion EPICS, tranche 3c part 1 (v2.96). ──
  expansionEpic('nightbloom', 'Nightbloom', 0, 2, 5, 0, 0, 3, {
    passive: 'moonlit-mending',
    blurb: 'It only opens after dark, and the ward wakes up whole.',
  }),
  expansionEpic('gloomcap', 'Gloomcap', 0, 1, 5, 0, 1, 2, {
    passive: 'sporebank',
    blurb: 'It grows on the successes of others, and one day everyone grows on its.',
  }),
  // ── 💜 Expansion EPICS, tranche 3c part 2 (v2.97). ──
  expansionEpic('quillhog', 'Quillhog', 0, 0, 2, 0, 5, 2, {
    passive: 'thorn-toll',
    blurb: 'Every fight it wins leaves the road gentler for the next one.',
  }),
  expansionEpic('aurora-serpent', 'Aurora Serpent', 0, 4, 1, 2, 0, 4, {
    passive: 'refraction-cascade',
    blurb: 'Its light does not stop at its own scales.',
  }),
  // ── 💜 Expansion EPICS, tranche 3d part 1 (v2.98). ──
  // v5.16 (per Javi): the first skill-carrier with a passive too —
  // Buried Treasure compounds the hoard that Plunder cashes out.
  expansionEpic('sable-corsair', 'Sable Corsair', 2, 3, 0, 1, 0, 5, {
    skill: 'plunder',
    passive: 'buried-treasure',
    blurb: 'It spends your fortune better than you would have.',
  }),
  expansionEpic('leviathan-calf', 'Leviathan Calf', 0, 5, 0, 0, 2, 4, {
    skill: 'deluge',
    blurb: 'Still a calf. The tide it brings is not.',
  }),
  // ── 💜 Expansion EPICS, tranche 3d part 2 (v2.99). ──
  expansionEpic('deepcurrent-oracle', 'Deepcurrent Oracle', 0, 5, 0, 1, 1, 3, {
    skill: 'foresight',
    blurb: 'It does not predict the future. It negotiates with it.',
  }),
  // ── 💜 Runestone Sentinel (v3.00): the first creature with BOTH an
  // on-use skill and a passive — Weathered exists to upgrade Bulwark
  // (per Javi: the one-or-the-other rule never applied to this pair). ──
  expansionEpic('runestone-sentinel', 'Runestone Sentinel', 0, 0, 0, 2, 5, 2, {
    skill: 'bulwark',
    passive: 'weathered',
    blurb: 'It has stood in front of worse than this, and intends to keep doing so.',
  }),
  // ── 💜 Marrowlight Jelly (v3.01): the second dual-carrier. ──
  expansionEpic('marrowlight-jelly', 'Marrowlight Jelly', 0, 4, 0, 3, 0, 3, {
    skill: 'luminesce',
    passive: 'bioluminance',
    blurb: 'Where it glows, the world offers you a little more to choose from.',
  }),
  // ── 💜 Stormforge Smith (v3.02): the Epic tier's last creature. ──
  expansionEpic('stormforge-smith', 'Stormforge Smith', 3, 0, 0, 3, 1, 3, {
    skill: 'reforge',
    blurb: 'Bring it a friend and a plan, and it will make the plan true.',
  }),
  // ── 🧡 Expansion LEGENDARIES, tranche 4a (v3.03). ──
  expansionLegendary('hoardwyrm', 'Hoardwyrm', 4, 0, 0, 0, 5, 2, {
    passive: 'dragons-hoard',
    blurb: 'Its strength is exactly as deep as your pockets.',
  }),
  expansionLegendary('tidemother-leviathan', 'Tidemother Leviathan', 0, 7, 0, 2, 0, 3, {
    passive: 'tide-deluge',
    blurb: 'When she rises, the whole ocean rises with her.',
  }),
  expansionLegendary('solstice-phoenix', 'Solstice Phoenix', 7, 0, 0, 2, 0, 5, {
    passive: 'rekindle',
    blurb: 'The end of you is merely the middle of it.',
  }),
  // ── 🧡 Expansion LEGENDARIES, tranche 4b (v3.04). ──
  expansionLegendary('aeon-sprout', 'Aeon Sprout, the Last Seed', 0, 1, 6, 1, 1, 3, {
    passive: 'millennia',
    blurb: 'One point of anything, and all the time in the world.',
  }),
  expansionLegendary('ouroboros', 'Ouroboros, the Turning Year', 2, 2, 2, 2, 2, 3, {
    passive: 'eternal-return',
    blurb: 'Whatever the year gave you, it gives again.',
  }),
  // ── 🧡 Expansion LEGENDARIES, tranche 4c (v3.05). ──
  expansionLegendary('mirricrow', 'Mirricrow', 2, 2, 2, 2, 2, 4, {
    skill: 'mimic',
    passive: 'mirri-understudy',
    blurb: 'It has studied everyone, and remembers whichever lesson you need.',
  }),
  expansionLegendary('thornheart-lily', 'Thornheart Lily', 1, 0, 6, 0, 2, 3, {
    passive: 'bloodbloom',
    blurb: 'Everything she grows is watered the same way.',
  }),
  // ── 🧡 Emberlord Kirin (v3.06): the roster's 68th and last. ──
  expansionLegendary('emberlord-kirin', 'Emberlord Kirin', 6, 0, 2, 1, 0, 4, {
    skill: 'decree',
    passive: 'sovereign',
    blurb: 'It does not fight the world. It informs the world of the outcome.',
  }),
  // ── 🦕 LEGACY (v5.95): the collection's capstone. Never rolled,
  // never sold — awakened by the first Achievement (discover every
  // creature) and then always pickable from the owned lane. Starts
  // humble on purpose: Burden of Ages IS the statline. Kit per Javi.
  {
    id: 'legacy',
    name: 'Legacy',
    stats: { fire: 1, water: 2, grass: 3, lightning: 5, earth: 4 },
    speed: 2,
    fight: 2,
    rarity: 'legendary',
    noSurge: true,
    awardOnly: true,
    passive: 'burden-of-ages',
    skill: 'balance-of-ages',
    blurb: 'It has carried every age on its back. Yours is simply next.',
  },
  // ── 🎩 THE HOMAGE CREATURES (v6.88+, kits settled with Javi 2026-09-10,
  // reports/sunday-docket-2026-09-13.md). Award-only like Legacy: the
  // chain-end achievements awaken them (HOMAGE_AWARDS below). Statlines
  // from the 2026-09-08 proposal (12-15 points; the kit is the creature).
  {
    id: 'mr-khool',
    name: 'Mr. Khool',
    stats: { fire: 0, water: 5, grass: 1, lightning: 1, earth: 5 },
    speed: 2,
    fight: 1,
    rarity: 'legendary',
    noSurge: true,
    awardOnly: true,
    trainable: ['water', 'earth'],
    passive: 'composure',
    skill: 'the-wager',
    blurb: 'A gentleman never loses his composure. He simply asks the dice again.',
  },
  {
    id: 'aid',
    name: 'Aid',
    stats: { fire: 0, water: 1, grass: 4, lightning: 5, earth: 0 },
    speed: 3,
    fight: 0,
    rarity: 'legendary',
    noSurge: true,
    awardOnly: true,
    trainable: ['lightning', 'grass'],
    passive: 'anticipation',
    skill: 'the-carriage',
    blurb: 'The valet is already where he is needed. He was there before you asked.',
  },
  {
    id: 'the-noble-one',
    name: 'The Noble One',
    stats: { fire: 2, water: 3, grass: 2, lightning: 4, earth: 2 },
    speed: 3,
    fight: 0,
    rarity: 'legendary',
    noSurge: true,
    awardOnly: true,
    passive: 'the-long-silence',
    skill: 'let-go',
    blurb: 'It has nothing to press and nothing to prove. Sit with it a while.',
  },
  {
    id: 'lanfeust',
    name: 'Lanf Starbane',
    stats: { fire: 1, water: 4, grass: 0, lightning: 6, earth: 2 },
    speed: 4,
    fight: 2,
    rarity: 'legendary',
    noSurge: true,
    awardOnly: true,
    passive: 'gravity',
    skill: 'blackhole',
    blurb: 'Everything falls toward it eventually. It is only a question of when.',
  },
  {
    id: 'robodude',
    name: 'Robodude',
    stats: { fire: 5, water: 0, grass: 0, lightning: 2, earth: 5 },
    speed: 1,
    fight: 3,
    rarity: 'legendary',
    noSurge: true,
    awardOnly: true,
    passive: 'siege-engine',
    skill: 'fire-support',
    blurb: 'It does not come home. Home is wherever it last stopped.',
  },
]

/** 🏆 v6.88: which chain-end achievement awakens which homage creature - defined in shared/achievements.ts
 * since v7.77, so the identity route can honour the avatars too. */
export { HOMAGE_AWARDS } from '../../shared/achievements'

/** 🎲 v5.95: what the random rollers may serve. Award-only creatures
 * (Legacy, the homage five) never enter it, so every roller draws from
 * this filtered pool — which equals the full pre-Legacy catalog exactly,
 * keeping seeded weekly/daily sequences byte-identical across releases. */
const ROLLABLE_POOL: CreatureDef[] = CREATURE_POOL.filter(c => !c.awardOnly)

/** 🎓 v6.88: may a level-up point land on this element? */
export function canTrainElement(def: Pick<CreatureDef, 'trainable'>, element: ElementKey): boolean {
  return !def.trainable || def.trainable.includes(element)
}

/** The free baseline pool (what the draft's random nine mostly rolls). */
export const BASE_CREATURES = CREATURE_POOL.filter(c => (c.rarity ?? 'uncommon') === 'uncommon')

/** The 🌟 Stardust roster. */
export const RARE_CREATURES = CREATURE_POOL.filter(c => c.rarity === 'rare')

/**
 * The player's COLLECTION as of the start of a run — purchases only
 * (v2.75, per Javi: "make the collection only account for purchased
 * creatures"). Before this, every uncommon was permanently free, which
 * made the shop's baseline tier unsellable. Snapshot lives in
 * RunState.unlockedCreatures.
 *
 * Note this is the *always-pickable* lane only. The draft's rolled
 * Wilds and in-run recruit rewards still draw from the whole catalog,
 * so a player who owns nothing can still field a full team.
 */
export function availableCreatures(unlocked: string[] | undefined): CreatureDef[] {
  const owned = new Set(unlocked ?? [])
  return CREATURE_POOL.filter(c => owned.has(c.id))
}

// ⚡ v4.89 (perf review #3): this lookup sits beneath creaturePassives,
// creatureSkill and every passive-tier check — the tick was paying
// ~72,000 linear-scan comparisons per second through it. A Map is O(1).
const CREATURE_BY_ID = new Map(CREATURE_POOL.map(d => [d.id, d]))

export function getCreatureDef(id: string): CreatureDef {
  const found = CREATURE_BY_ID.get(id)
  if (!found) throw new Error(`Unknown creature def: ${id}`)
  return found
}

/**
 * Draws `count` DISTINCT creatures from `pool`, weighted by rarity.
 *
 * `owned` (when given) applies the v2.75 unowned bias: anything not in
 * the set rolls at `unownedDraftBias`×, so the lane keeps showing the
 * player things they could buy instead of decaying into a list of what
 * they already have.
 *
 * `rng` lets a caller replay the same draw — the 🌟 shop passes an
 * hour-seeded generator.
 */
function rollWeighted(
  pool: readonly CreatureDef[],
  count: number,
  opts: {
    owned?: ReadonlySet<string>
    rng?: () => number
    weights?: Record<string, number>
  } = {},
): string[] {
  const { owned, rng, weights = TUNING.creatures.draftWeights } = opts
  const bias = TUNING.creatures.unownedDraftBias
  const remaining = [...pool]
  const picked: string[] = []
  while (picked.length < count && remaining.length > 0) {
    const choice = weightedPick(remaining.map((c) => {
      const weight = weights[c.rarity ?? 'uncommon'] ?? 0
      return [c, owned && !owned.has(c.id) ? weight * bias : weight] as const
    }), rng)
    remaining.splice(remaining.indexOf(choice), 1)
    picked.push(choice.id)
  }
  return picked
}

/**
 * The draft's eight random Wilds (v1.01, retuned v2.75): rolled from
 * the WHOLE catalog weighted by rarity — mostly baseline creatures with
 * the occasional rare as a lucky tease. Free to pick whether or not the
 * player owns them, which makes the lane a one-run trial of the shop;
 * purchased creatures are ALWAYS pickable from their own draft section
 * regardless. Distinct.
 *
 * `owned` biases the roll toward creatures the player has NOT bought.
 */
export function rollDraftOptions(
  count: number = TUNING.draft.poolSize,
  owned?: readonly string[],
  cover: readonly (readonly ElementKey[])[] = [],
): string[] {
  const ownedSet = owned ? new Set(owned) : undefined
  // 🎯 v7.33 (per Javi: "my draft had poor options" against a balanced
  // first row): for every starting node's posted pair, ONE card whose
  // two strongest elements ARE that pair rides in the pool (still a
  // rarity-weighted, unowned-biased draw within that slice - every pair
  // has at least three such creatures). The rest of the pool stays the
  // random Wilds; the whole hand is shuffled so the guarantees do not
  // sit in the first slots.
  const picked: string[] = []
  const seen = new Set<string>()
  for (const pair of Array.isArray(cover) ? cover : []) {
    if (pair.length !== 2) continue
    const key = [...pair].sort().join('+')
    if (seen.has(key) || picked.length >= count) continue
    seen.add(key)
    const slice = ROLLABLE_POOL.filter(c => !picked.includes(c.id) && topPairOf(c) === key)
    if (slice.length === 0) continue
    picked.push(...rollWeighted(slice, 1, { owned: ownedSet }))
  }
  const rest = rollWeighted(ROLLABLE_POOL.filter(c => !picked.includes(c.id)), count - picked.length, { owned: ownedSet })
  return shuffle([...picked, ...rest])
}

/** The two strongest elements of a def, as a sorted `a+b` key (one element
 * alone for a mono creature). The draft-cover rule matches nodes on it. */
export function topPairOf(def: Pick<CreatureDef, 'stats'>): string {
  return ELEMENTS.filter(e => def.stats[e] > 0)
    .sort((a, b) => def.stats[b] - def.stats[a])
    .slice(0, 2)
    .sort()
    .join('+')
}

/**
 * Recruit-reward options (v2.75). Rolled rarity-weighted from the whole
 * catalog rather than from the collection: recruits are an in-run
 * reward, the same category as a Wilds card, so gating them behind
 * purchases would leave a new account with no recruits at all.
 */
export function rollRecruitOptions(count: number, taken: readonly string[] = [], run?: RunState): string[] {
  const exclude = new Set(taken)
  const pool = ROLLABLE_POOL.filter(c => !exclude.has(c.id))
  if (!run) return rollWeighted(pool, count)
  // 🔁 v5.17 (per Javi): passed-over recruits sit out until the rest of
  // the catalog has had its turn on a card — targets can be farmed.
  return drawThroughCycle(run, 'recruit', pool.map(c => c.id), count, (ids, n) => {
    const subset = new Set(ids)
    return rollWeighted(pool.filter(c => subset.has(c.id)), n)
  })
}

/**
 * 🌟 The rotating shop shelf (v2.75, per Javi): five creatures of any
 * rarity, re-rolled every hour.
 *
 * The shelf is a rarity-weighted draw seeded on the refresh bucket (12h
 * since v3.77), which
 * makes it identical across reloads and devices and impossible to
 * re-roll by refreshing — with no persisted state of its own.
 *
 * v2.76 (per Javi): it does NOT refill. Buying something marks that
 * slot owned and leaves it there; the hour is the only thing that
 * changes the stock. The shelf is a fixed offer, not a vending machine
 * you can drain in one sitting.
 */
export function shopBucket(now = Date.now()): number {
  return Math.floor(now / TUNING.creatures.shopRefreshMs)
}

/** 🎴 Booster pack roll (v3.20): rarity-weighted like the shelf (the
 * pack is deliberate acquisition too), unseeded — every pack is a fresh
 * gamble. Distinct within the pack; duplicates against the COLLECTION
 * are the refund case. */
export function rollBoosterPack(count = TUNING.creatures.booster.cards): string[] {
  return rollWeighted(ROLLABLE_POOL, count, { weights: TUNING.creatures.shopWeights })
}

/**
 * 🌟 What a creature costs on the shelf (v3.62): its RARITY's price, not
 * a flat rate. Unknown/legacy rarities fall back to uncommon so a new
 * tier can never accidentally ship free.
 */
export function creaturePrice(rarity: CreatureDef['rarity']): number {
  const prices = TUNING.creatures.prices
  return prices[rarity ?? 'uncommon'] ?? prices.uncommon ?? 0
}

/** The same, by id — for callers holding only a defId. */
export function creaturePriceOf(defId: string): number {
  return creaturePrice(getCreatureDef(defId).rarity)
}

/**
 * Stardust returned per duplicate in a booster (v3.20; rarity-aware since
 * v3.62). A legendary duplicate hurting exactly as much as an uncommon
 * one was the worst part of opening a late pack.
 */
export function boosterDupeRefund(rarity: CreatureDef['rarity']): number {
  const pcts = TUNING.creatures.booster.dupeRefundPct
  const pct = pcts[rarity ?? 'uncommon'] ?? pcts.uncommon ?? 0
  // round, not ceil: 2500 × 0.56 floats to 1400.0000000000002, and the
  // legendary rung must land EXACTLY on two pack prices (v4.34).
  return Math.round(creaturePrice(rarity) * pct)
}

/**
 * 🎲 Booster pull odds, PER CARD, as percentages by rarity (v3.77).
 *
 * Apple (App Review 3.1.1) and Google Play both require the odds of a
 * randomised paid item to be disclosed to the player BEFORE purchase, in
 * the app itself — a store listing or a website does not satisfy it. The
 * requirement attaches once the currency is purchasable; stardust is
 * earned-only today, but the ladder in gamedesign.md would trip it, so
 * the disclosure ships ahead of the payment code rather than behind it.
 *
 * DERIVED from the live pool and weights, never a written-down table: a
 * hardcoded figure that drifts from the actual roll is precisely the
 * thing these policies exist to stop. Pair it with the test that samples
 * real packs and checks the published numbers against them.
 */
export function boosterOdds(): Array<{ rarity: string; pct: number }> {
  const weights = TUNING.creatures.shopWeights as Record<string, number>
  const mass: Record<string, number> = {}
  let total = 0
  // 🧮 v7.77: the pool packs actually draw from - the six award-only legendaries counted here and showed the
  // legendary line at 2.43% while packs dealt ~1.58%.
  for (const def of ROLLABLE_POOL) {
    const rarity = def.rarity ?? 'uncommon'
    const w = weights[rarity] ?? 0
    mass[rarity] = (mass[rarity] ?? 0) + w
    total += w
  }
  const order = ['legendary', 'epic', 'rare', 'uncommon']
  return order
    .filter(r => (mass[r] ?? 0) > 0)
    .map(rarity => ({ rarity, pct: (mass[rarity]! / total) * 100 }))
}

/** What a pack costs (v3.62: a flat price, no longer a multiple). */
export function boosterPrice(): number {
  return TUNING.creatures.booster.price
}

export function rollShopStock(bucket: number, count = TUNING.creatures.shopStock): string[] {
  // Mixed by a large odd constant so adjacent hours look unrelated.
  const rng = seededRandom(Math.imul(bucket, 2654435761))
  return rollWeighted(ROLLABLE_POOL, count, { rng, weights: TUNING.creatures.shopWeights })
}
